import type { ReactNode } from 'react';
import { format, type Money, type TowBreakdown } from '@evogard/domain';
import { km } from './assistance.js';

/**
 * A conta do reboque aberta em linhas (02/11/2026) — a mesma no Resumo do Novo
 * acionamento e na ficha. O percurso é o inteiro do prestador, da base até o
 * retorno; até a cobertura do plano a Evogard paga, e o que passa dela o
 * associado paga direto ao prestador, ao valor do km excedente da tabela. O
 * adicional (o patins) é sempre da Evogard.
 *
 * Os números saem de `towBreakdown` (`@evogard/domain`), a mesma conta que
 * `dispatchAmount` paga: a tela não mostra um valor que o pagamento não paga.
 */
export function TowBreakdownLines({
  breakdown: b,
  addon,
  evogardTotal,
  footer,
}: {
  breakdown: TowBreakdown;
  addon: { name: string; amount: Money } | null;
  /** O que a Evogard paga, já com o adicional. */
  evogardTotal: Money;
  /** O que vem depois da parte do associado — na ficha, o "ciente" dele. */
  footer?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <dl className="flex flex-col gap-1">
        <Line label="Percurso do prestador (base → retorno)" value={km(b.totalM)} />
        <Line label={`Saída (cobre ${km(b.includedM)})`} value={format(b.flatAmount)} />
        {b.coveredExtraM > 0 && (
          <Line
            label={`${km(b.coveredExtraM)} além da saída × ${format(b.perKmAmount)}`}
            value={format(b.coveredExtraAmount)}
          />
        )}
        {addon && <Line label={addon.name} value={format(addon.amount)} />}
        <div className="mt-1 flex justify-between gap-3 border-t border-borda pt-2">
          <dt className="font-semibold">A Evogard paga</dt>
          <dd className="font-bold tabular-nums">{format(evogardTotal)}</dd>
        </div>
      </dl>
      {b.associate > 0 ? (
        <div className="flex flex-col gap-1 rounded-lg border border-alerta/50 bg-alerta-suave p-3">
          <div className="flex justify-between gap-3">
            <span className="font-semibold">O associado paga ao prestador</span>
            <b className="tabular-nums">{format(b.associate)}</b>
          </div>
          <p className="text-xs text-texto-suave">
            {km(b.excessM)} além dos {km(b.paidUpToM)} pagos pela Evogard ×{' '}
            {format(b.perKmAmount)} o km excedente do prestador.
            {b.coverageM !== null &&
              b.paidUpToM !== null &&
              b.paidUpToM > b.coverageM &&
              ` A saída do prestador cobre mais que o plano (${km(b.coverageM)}), e é paga inteira.`}
          </p>
          {footer}
        </div>
      ) : (
        <p className="text-xs text-texto-suave">
          {b.coverageM === null
            ? 'Cobertura sem limite: nada fica com o associado.'
            : `Dentro da cobertura de ${km(b.coverageM)}: nada fica com o associado.`}
        </p>
      )}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-texto-suave">{label}</dt>
      <dd className="whitespace-nowrap tabular-nums">{value}</dd>
    </div>
  );
}
